import React, { useCallback } from "react";
import {
  View,
  FlatList,
  TextInput,
  ActivityIndicator,
  StyleSheet,
} from "react-native";
import DsHeader from "@components/ds/DsHeader";
import { useSearchableList } from "@components/ds/useSearchableList";
import { C, RText, Tag, Search, Warning } from "@components/ds/theme";
import { t } from "@constants/localization";
import { getReceivingTps } from "@services/api/receiving";
import RackTpRow from "../components/RackTpRow";
import {
  BUCKET,
  BUCKET_META,
  getTpCardKey,
  getTpCta,
  shortDate,
  withAdvanceDateHeaders,
} from "../helpers";

const PATH = "Receiving/bucket";

const EMPTY = { bucket: null, progress: null, dates: [], tps: [] };

// Per-bucket colours from the mockup (§9 frames 02–04): the progress pill,
// "to rack" count, bar, and (Advance / Pending) the note banner.
const LOOK = {
  [BUCKET.TODAY]: {
    pillBg: C.greenBg,
    pillInk: C.green,
    toRackInk: C.red,
    bar: C.green,
  },
  [BUCKET.ADVANCE]: {
    pillBg: C.pendingBg,
    pillInk: C.pending,
    toRackInk: C.pending,
    bar: C.orange,
    noteBg: C.orangeBg,
    noteLine: C.orange,
    noteInk: C.pending,
  },
  [BUCKET.PENDING]: {
    pillBg: C.redBg,
    pillInk: C.red,
    toRackInk: C.red,
    bar: C.red,
    noteBg: C.redBg,
    noteLine: C.red,
    noteInk: C.redText,
  },
};

const percentOf = (racked, total) =>
  total > 0 ? `${Math.round((racked / total) * 100)}%` : "0%";

// One racking-hub list, pixel-matched to the mockup (§9 frames 02–04),
// opened from ReceivingHub with `bucket`: TODAY (live for DB picking once
// racked), ADVANCE (future EDDs racked ahead, by date, hidden from the DB)
// or PENDING (overdue).
const ReceivingBucketList = ({ navigation, route }) => {
  const bucket = BUCKET_META[route?.params?.bucket]
    ? route.params.bucket
    : BUCKET.TODAY;
  const meta = BUCKET_META[bucket];
  const look = LOOK[bucket];

  const fetchList = useCallback(
    async ({ search }) => {
      const { data } = await getReceivingTps({ bucket, search });
      return data?.data || EMPTY;
    },
    [bucket],
  );

  const {
    data,
    searchText,
    onChangeSearchText,
    isLoading,
    isRefreshing,
    onRefresh,
  } = useSearchableList(fetchList, { initialData: EMPTY, deps: [bucket] });

  const onRowPress = (tp) => {
    const cta = getTpCta(tp);
    const params = {
      cx_id: tp.cx_id,
      date: tp.date,
      cx_name: tp.cx_name,
      area: tp.area,
      shipment_count: tp.shipment_count,
    };
    if (cta.action === "RACK") navigation.navigate("ReceivingScanRack", params);
    else if (cta.action === "CONTINUE" || cta.action === "DONE") {
      navigation.navigate("ReceivingScanShipments", params);
    }
  };

  const progress = data?.progress || {};
  const racked = Number(progress.racked ?? 0);
  const total = Number(progress.total ?? 0);
  const restText =
    bucket === BUCKET.PENDING
      ? t(`${PATH}/overdue`)
      : t(`${PATH}/racked`, { racked: String(racked), total: String(total) });
  const fill = { width: percentOf(racked, total), backgroundColor: look.bar };
  const noteStyle = look.noteBg
    ? { backgroundColor: look.noteBg, borderColor: look.noteLine }
    : null;

  const tps = data?.tps || [];
  const rows =
    bucket === BUCKET.ADVANCE ? withAdvanceDateHeaders(tps, data?.dates) : tps;
  const body = rows.length ? rows : isLoading ? [] : [{ _key: "EMPTY" }];

  const listHeader = (
    <View style={styles.header}>
      <View style={styles.progressCard}>
        <View style={styles.progressTop}>
          <View style={styles.grow}>
            <RText size={9} weight={700} color={C.muted} style={styles.label}>
              {t(`${PATH}/${meta.key}Label`)}
            </RText>
            <RText size={15} weight={700} style={styles.progressValue}>
              <RText size={15} weight={700} color={look.toRackInk}>
                {t(`${PATH}/toRack`, {
                  count: String(progress.to_rack ?? 0),
                })}
              </RText>
              <RText size={15} weight={500} color={C.muted}>
                {restText}
              </RText>
            </RText>
          </View>
          <Tag label={meta.tag} bg={look.pillBg} color={look.pillInk} />
        </View>
        <View style={styles.track}>
          <View style={[styles.fill, fill]} />
        </View>
      </View>
      <View style={styles.search}>
        <Search />
        <TextInput
          testID="receiving-bucket-search-input"
          value={searchText}
          onChangeText={onChangeSearchText}
          placeholder={t(`${PATH}/searchPlaceholder`)}
          placeholderTextColor={C.disabledInk}
          style={styles.searchInput}
          autoCorrect={false}
          returnKeyType="search"
        />
      </View>
      {noteStyle ? (
        <View style={[styles.note, noteStyle]}>
          <Warning color={look.noteLine} />
          <RText
            size={12}
            weight={500}
            color={look.noteInk}
            style={styles.noteText}
          >
            {t(`${PATH}/${meta.key}Note`)}
          </RText>
        </View>
      ) : null}
    </View>
  );

  const renderItem = ({ item }) => {
    if (item._key === "EMPTY") {
      return (
        <RText size={14} weight={500} color={C.muted} style={styles.empty}>
          {t(`${PATH}/empty`)}
        </RText>
      );
    }
    if (item._type === "ADVANCE_DATE_HEADER") {
      return (
        <RText size={9} weight={700} color={C.muted} style={styles.label}>
          {t(`${PATH}/dateHeader`, {
            date: shortDate(item.date).toUpperCase(),
            count: String(item.count),
          })}
        </RText>
      );
    }
    return <RackTpRow item={item} bucket={bucket} onPress={onRowPress} />;
  };

  const title =
    bucket === BUCKET.TODAY
      ? t(`${PATH}/todayTitle`, { date: shortDate(route?.params?.date) })
      : t(`${PATH}/${meta.key}Title`);

  return (
    <View style={styles.screen}>
      <DsHeader
        title={title}
        navigation={navigation}
        testID="receiving-bucket-back"
      />
      {isLoading && tps.length === 0 ? (
        <ActivityIndicator size="large" color={C.red} style={styles.loader} />
      ) : (
        <FlatList
          testID={`receiving-bucket-list-${bucket}`}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
          data={body}
          keyExtractor={(item) => item._key || getTpCardKey(item)}
          renderItem={renderItem}
          ListHeaderComponent={listHeader}
          refreshing={isRefreshing}
          onRefresh={onRefresh}
          contentContainerStyle={styles.listBody}
        />
      )}
    </View>
  );
};

export default ReceivingBucketList;

const styles = StyleSheet.create({
  screen: { flex: 1, backgroundColor: C.page },
  loader: { flex: 1 },
  listBody: {
    paddingTop: 14,
    paddingHorizontal: 16,
    paddingBottom: 24,
    gap: 12,
  },
  header: { gap: 12 },
  progressCard: {
    backgroundColor: C.surface,
    borderWidth: 1,
    borderColor: C.border,
    borderRadius: 12,
    padding: 14,
  },
  progressTop: { flexDirection: "row", alignItems: "center", gap: 10 },
  grow: { flex: 1 },
  label: { letterSpacing: 0.6 },
  progressValue: { paddingTop: 2 },
  track: {
    height: 7,
    backgroundColor: C.track,
    borderRadius: 5,
    overflow: "hidden",
    marginTop: 10,
  },
  fill: { height: "100%", borderRadius: 5 },
  search: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    borderWidth: 1,
    borderColor: C.border,
    borderRadius: 10,
    paddingHorizontal: 14,
    backgroundColor: C.surface,
  },
  searchInput: {
    flex: 1,
    fontSize: 15,
    color: C.ink,
    paddingVertical: 12,
  },
  note: {
    flexDirection: "row",
    gap: 10,
    borderWidth: 1,
    borderRadius: 10,
    paddingVertical: 12,
    paddingHorizontal: 13,
  },
  noteText: { flex: 1, lineHeight: 18 },
  empty: { textAlign: "center", marginTop: 60 },
});
